import { Avatar, CircularProgress } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Box from '@mui/material/Box';
import { Modal } from '@mui/material';
import moment from 'moment';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import ManIcon from '@mui/icons-material/Man';
import WomanIcon from '@mui/icons-material/Woman';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';

import { InfluencerUpdateProfile, BrandConnectUpdateProfile } from 'api_handlers/discovery';
import React, { useState, useEffect } from 'react';
import CountDisplay from 'components/ux/CountDisplay';
import { getProfileUpdateStatus } from 'api_handlers/planner2';
import CollaborationHistory from '../CollaborationHistory';
import ContactModal from './ContactModal';
import { getCurrentUser } from 'api_handlers/auth';
import { ui, platformPalette } from '../shared/insightUi';

const AGE_BUCKET_TO_LABEL = {
  age18To24: '18-24',
  age25To34: '25-34',
  age35To44: '35-44',
  age45To54: '45-54',
  age55To64: '55-64',
  age65ToInf: '65+',
};

function formatAgeDisplay(age) {
  if (!age) return null;
  const bucket = age.bucket || age.code;
  if (!bucket) return null;
  return AGE_BUCKET_TO_LABEL[bucket] || bucket.replace(/^age/, '').replace(/To/g, '-');
}

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#3A3A3A',
    color: 'white',
    borderRadius: '2px',
    maxWidth: 220,
    width: 120,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
  arrow: {
    color: '#3A3A3A',
  },
}))(Tooltip);

/* ── Presentational helpers ─────────────────────────────────────────────── */

const badgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 11,
  fontWeight: 700,
  padding: '3px 10px',
  borderRadius: 999,
  fontFamily: ui.font,
  whiteSpace: 'nowrap',
};

function ActionButton({ icon, children, onClick, background, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        cursor: 'pointer',
        border: 'none',
        borderRadius: 10,
        background,
        color,
        padding: '9px 14px',
        fontSize: 12.5,
        fontWeight: 700,
        fontFamily: ui.font,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.10)',
        width: '100%',
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function SnapshotRow({ icon, label, value, pct }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
      <span
        style={{
          width: 28,
          height: 28,
          flex: '0 0 auto',
          borderRadius: 8,
          background: '#FFFFFF',
          border: `1px solid ${ui.border}`,
          color: ui.primary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: ui.inkFaint, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, fontWeight: 800, color: ui.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: ui.primary }}>{pct}</div>
    </div>
  );
}

/* ── Component ──────────────────────────────────────────────────────────── */

function BasicInfo({
  name,
  picture,
  interests = [],
  url,
  audience_short_desc = {},
  platform = 'instagram',
  prediction,
  primary_category = '',
  last_updated,
  id,
  influencer,
  is_staff,
  past_collaborations,
  update_status,
  isClicked,
  updateStatus,
  getId,
  location,
  country,
  email_details,
  contact_details,
  contact_details_planner,
  is_brandconnect,
  bc_insight,
  bc_insight_60,
  bc_insight_90,
  bc_insight_lifetime,
  dump,
  bc_public_data,
  description,
  period = '28',
}) {
  const bcSource =
    period === '60'
      ? bc_insight_60
      : period === '90'
        ? bc_insight_90
        : period === 'lifetime'
          ? bc_insight_lifetime
          : bc_insight;
  const genderDist = bcSource?.channelStats?.OVERALL?.audienceGenderDistribution;
  const derivedGender = !audience_short_desc?.gender?.code && genderDist
    ? (genderDist.male >= (genderDist.female || 0) ? { code: 'MALE', percentage: `${Math.round((genderDist.male || 0) * 100)}%` } : { code: 'FEMALE', percentage: `${Math.round((genderDist.female || 0) * 100)}%` })
    : audience_short_desc?.gender;
  const [isDisabled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // const [status, setStatus] = useState(update_status);
  const [status, setStatus] = useState(is_brandconnect === 'yes' ? 'updated' : update_status);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [openCollabModal, setOpenCollabModal] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const pollTimerRef = React.useRef(null);
  const pollDeadlineRef = React.useRef(0);
  const pollErrorsRef = React.useRef(0);

  // First check fires immediately, then every 15s. A profile that is still
  // "updating" after 15 minutes is treated as failed so the spinner cannot
  // hang forever when a Celery worker dies mid-task.
  const POLL_INTERVAL_MS = 15000;
  const POLL_TIMEOUT_MS = 15 * 60 * 1000;
  const POLL_MAX_CONSECUTIVE_ERRORS = 3;
  const date = last_updated !== undefined ? new Date(last_updated) : null;
  const dataSharingEnabled =
    String(bc_public_data?.dataSharingState || '')
      .toUpperCase()
      .includes('ENABLED') ||
    String(bc_public_data?.dataSharingState || '')
      .toUpperCase()
      .includes('GIVEN') ||
    String(dump?.data_sharing_state || '')
      .toUpperCase()
      .includes('ENABLED') ||
    String(dump?.data_sharing_state || '')
      .toUpperCase()
      .includes('GIVEN');

  async function handleApi() {
    if (!isDisabled) {
      try {
        setIsUpdating(true);
        setStatus('updating');

        let response;

        if (is_brandconnect === 'yes') {
          response = await BrandConnectUpdateProfile(id, influencer, platform);
        } else {
          response = await InfluencerUpdateProfile(id, influencer, platform);
        }

        // 202 is the queued-for-Celery response; the backend no longer
        // claims the profile is updated at dispatch time.
        if ([200, 201, 202].includes(response.status)) {
          updateStatusApi();
        } else {
          setIsUpdating(false);
          setStatus('failed');
        }
      } catch (err) {
        console.error(err);
        setIsUpdating(false);
        setStatus('failed');
      }
    }
  }

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const finishPolling = (finalStatus, notifyParent) => {
    stopPolling();
    setStatus(finalStatus);
    setIsUpdating(false);
    if (notifyParent) {
      isClicked(true);
    }
  };

  const pollOnce = () => {
    getProfileUpdateStatus(getId)
      .then((response) => {
        pollErrorsRef.current = 0;
        if (response.update_status === 'updated' || response.update_status === 'failed') {
          // Only now is the freshly written last_updated safe to read, so
          // this is the only place the parent is asked to refetch.
          finishPolling(response.update_status, true);
          return;
        }
        if (Date.now() > pollDeadlineRef.current) {
          finishPolling('failed', false);
        }
      })
      .catch((err) => {
        console.error('Error fetching update status', err);
        pollErrorsRef.current += 1;
        // Tolerate a transient blip; one bad response used to kill polling
        // permanently and leave the row stuck on "Updating...".
        if (pollErrorsRef.current >= POLL_MAX_CONSECUTIVE_ERRORS) {
          finishPolling('failed', false);
        }
      });
  };

  const updateStatusApi = () => {
    stopPolling();
    pollErrorsRef.current = 0;
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    pollOnce();
    pollTimerRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    setIsUpdating(updateStatus || status === 'updating');
    if (status === 'updated' || status === 'failed') {
      isClicked(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStatus, status]);

  useEffect(() => stopPolling, []);

  useEffect(() => {
    getCurrentUser().then((data) => setIsStaff(data.data.is_staff));
  }, []);

  /* ── Derived, display-only values ── */
  const palette = platformPalette(platform);
  const displayName = bc_public_data?.title || name;
  const creatorTypeLabel = prediction?.creator_type
    ? `${prediction.creator_type[0]?.toUpperCase()}${prediction.creator_type.slice(1).toLowerCase()} Influencer`
    : 'Influencer';
  const subLine = [dump?.vanity_name, country || dump?.country || bc_public_data?.regionCode]
    .filter(Boolean)
    .join(' • ');
  const bio = description || bc_public_data?.description || '';

  const topicChips =
    is_brandconnect === 'yes' && bc_public_data?.topicCategories?.length > 0
      ? bc_public_data.topicCategories.slice(0, 6).map((u) => {
          const match = typeof u === 'string' && u.match(/\/wiki\/(.+)$/);
          return match ? decodeURIComponent(match[1]).replace(/_/g, ' ') : u;
        })
      : [];

  const genderLabel =
    derivedGender?.code === 'MALE' ? 'Male'
    : derivedGender?.code === 'FEMALE' ? 'Female'
    : derivedGender?.code || '–';
  const ageLabel = formatAgeDisplay(audience_short_desc?.age)
    ? `${formatAgeDisplay(audience_short_desc.age)} yrs`
    : '–';
  const hasSnapshot =
    audience_short_desc?.location?.name ||
    audience_short_desc?.location?.bucket ||
    derivedGender?.code ||
    formatAgeDisplay(audience_short_desc?.age);

  const PlatformIcon = platform === 'youtube' ? YouTubeIcon : InstagramIcon;

  return (
    // div, not <section>: a globally-leaked stylesheet restyles <section>.
    <div
      style={{
        background: ui.card,
        border: `1px solid ${ui.border}`,
        borderRadius: ui.radius,
        boxShadow: ui.shadow,
        overflow: 'hidden',
        fontFamily: ui.font,
        textAlign: 'left',
      }}
    >
      {contact_details_planner && isStaff && openContactModal ? (
        <ContactModal
          open={openContactModal}
          close={(val) => setOpenContactModal(val)}
          name={name}
          email={
            bc_public_data?.businessEmail
              ? [bc_public_data.businessEmail].filter(Boolean)
              : contact_details_planner
                  .filter((data) => ['email'].includes(data.type))
                  .map((val) => val.formatted_value)
          }
          contact={contact_details_planner
            .filter((data) => ['phone'].includes(data.type))
            .map((val) => val.formatted_value)}
          insta={contact_details_planner
            .filter((data) => ['instagram'].includes(data.type))
            .map((val) => val.formatted_value)}
          yt={contact_details_planner
            .filter((data) => ['youtube'].includes(data.type))
            .map((val) => val.formatted_value)}
          thread={contact_details_planner
            .filter((data) => ['thread', 'threads'].includes((data.type || '').toLowerCase()))
            .map((val) => val.formatted_value)}
          twitter={contact_details_planner
            .filter((data) => ['twitter'].includes(data.type))
            .map((val) => val.formatted_value)}
          facebook={contact_details_planner
            .filter((data) => ['facebook'].includes(data.type))
            .map((val) => val.formatted_value)}
          whatsapp={contact_details_planner
            .filter((data) => ['whatsapp'].includes(data.type))
            .map((val) => val.formatted_value)}
          socialLinks={bc_public_data?.socialLinks || []}
        ></ContactModal>
      ) : (
        ''
      )}
      <Modal open={openCollabModal} onClose={() => setOpenCollabModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: 28,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'min(540px, 92vw)',
            maxHeight: 'calc(100vh - 64px)',
            overflowY: 'auto',
            bgcolor: 'transparent',
            outline: 'none',
          }}
        >
          <CollaborationHistory
            past_collaborations={past_collaborations}
            is_staff={isStaff}
            onClose={() => setOpenCollabModal(false)}
          />
        </Box>
      </Modal>

      {/* ── Cover band ── */}
      <div style={{ height: 128, background: palette.cover, position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            top: 18,
            left: 24,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            background: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.25)',
            backdropFilter: 'blur(8px)',
            color: '#FFFFFF',
            borderRadius: 999,
            padding: '5px 13px',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.3,
          }}
        >
          <PlatformIcon style={{ fontSize: 15 }} />
          {palette.label}
        </span>
        {date && !isNaN(date.getTime()) && (
          <span
            style={{
              position: 'absolute',
              top: 18,
              right: 24,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'rgba(255,255,255,0.85)',
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            <HistoryRoundedIcon style={{ fontSize: 14 }} />
            Updated {moment(date).format('DD MMM YYYY, HH:mm')}
          </span>
        )}
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '0 28px 26px', display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {/* Left: identity */}
        <div style={{ flex: '1 1 460px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginTop: -48 }}>
            <Avatar
              alt={displayName}
              src={picture || bc_public_data?.thumbnailUri}
              style={{
                height: 128,
                width: 128,
                border: '4px solid #FFFFFF',
                boxShadow: '0 8px 24px rgba(16, 24, 40, 0.18)',
                flex: '0 0 auto',
              }}
            />
            <div style={{ minWidth: 0, paddingBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 800,
                    color: ui.ink,
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {displayName}
                </h1>
                {is_brandconnect === 'yes' && (
                  <VerifiedRoundedIcon style={{ fontSize: 20, color: '#2563EB' }} titleAccess="YCP Verified" />
                )}
                {url && (
                  <button
                    type="button"
                    onClick={() => window.open(url, '_blank')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      cursor: 'pointer',
                      borderRadius: 999,
                      border: `1px solid ${ui.border}`,
                      background: '#FFFFFF',
                      color: palette.accent,
                      padding: '4px 12px',
                      fontSize: 11.5,
                      fontWeight: 700,
                      fontFamily: ui.font,
                    }}
                  >
                    View profile
                    <OpenInNewRoundedIcon style={{ fontSize: 13 }} />
                  </button>
                )}
              </div>
              <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: ui.inkSoft }}>
                {creatorTypeLabel}
                {subLine && (
                  <span style={{ fontWeight: 600, color: ui.inkFaint }}> • {subLine}</span>
                )}
              </div>
            </div>
          </div>

          {/* Trust badges */}
          {is_brandconnect === 'yes' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <span style={{ ...badgeBase, background: '#DC2626', color: '#FFFFFF' }}>
                ✓ YCP Verified
              </span>
              {dataSharingEnabled && (
                <span style={{ ...badgeBase, background: '#DCFCE7', color: '#166534' }}>
                  Data sharing enabled
                </span>
              )}
              {(bc_public_data?.shoppingAffiliateState?.includes('ENABLED') ||
                dump?.shopping_affiliate_state?.includes('ENABLED')) && (
                <span style={{ ...badgeBase, background: '#E0E7FF', color: '#3730A3' }}>
                  Shopping affiliate
                </span>
              )}
            </div>
          )}

          {/* Category chips */}
          {(topicChips.length > 0 || (is_brandconnect !== 'yes' && primary_category)) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {(topicChips.length > 0 ? topicChips : [primary_category]).filter(Boolean).map((label, i) => (
                <span
                  key={i}
                  style={{
                    ...badgeBase,
                    background: palette.soft,
                    color: palette.accent,
                    border: `1px solid ${palette.accent}22`,
                    padding: '4px 12px',
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          )}

          {/* Bio */}
          {bio && (
            <p
              title={bio}
              style={{
                margin: '14px 0 0',
                maxWidth: 560,
                fontSize: 13,
                lineHeight: 1.55,
                color: ui.inkSoft,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {bio}
            </p>
          )}

          {/* Staff: update profile */}
          {is_staff ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  if (!isUpdating) handleApi();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: isDisabled || isUpdating ? 'default' : 'pointer',
                  borderRadius: 999,
                  border: `1px solid ${isDisabled ? '#D0D5DD' : '#FDBA74'}`,
                  background: isDisabled ? '#F9FAFB' : '#FFF7ED',
                  color: isDisabled ? '#666666' : '#EA580C',
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: ui.font,
                }}
              >
                {isUpdating ? (
                  <>
                    <CircularProgress style={{ height: 13, width: 13, color: '#EA580C' }} />
                    Updating...
                  </>
                ) : (
                  <>
                    <AutorenewIcon style={{ fontSize: 15 }} />
                    Update profile
                  </>
                )}
              </button>
              {isDisabled && status === 'updated' && !isUpdating ? (
                <CountDisplay
                  tooltipInfo={
                    <React.Fragment>
                      <div>
                        <b>Wait untill 24 hours</b>
                      </div>
                    </React.Fragment>
                  }
                ></CountDisplay>
              ) : (
                ''
              )}
              {status === 'failed' ? (
                <HtmlTooltip title="Profile Updating failed">
                  <span style={{ cursor: 'pointer', display: 'inline-flex', color: '#DC2626' }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </span>
                </HtmlTooltip>
              ) : (
                ''
              )}
            </div>
          ) : (
            ''
          )}
        </div>

        {/* Right: actions + audience snapshot */}
        <div style={{ flex: '0 1 280px', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
          {isStaff && (
            <ActionButton
              icon={<ContactsRoundedIcon style={{ fontSize: 15 }} />}
              onClick={() => is_staff && setOpenContactModal(true)}
              background="#FEBD1C"
              color="#1F2937"
            >
              Contact Details
            </ActionButton>
          )}
          {Array.isArray(past_collaborations) && past_collaborations.length > 0 && (
            <ActionButton
              icon={<HistoryRoundedIcon style={{ fontSize: 15 }} />}
              onClick={() => setOpenCollabModal(true)}
              background="#5B5FC7"
              color="#FFFFFF"
            >
              Collaboration History
            </ActionButton>
          )}
          {hasSnapshot && (
            <div
              style={{
                background: ui.tileBg,
                border: `1px solid ${ui.border}`,
                borderRadius: ui.radiusSm,
                padding: '14px 16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 12.5, fontWeight: 800, color: ui.ink, letterSpacing: 0.3 }}>
                  Audience snapshot
                </span>
                <InsightsRoundedIcon style={{ fontSize: 15, color: ui.primary }} />
              </div>
              <SnapshotRow
                icon={<LocationOnIcon style={{ fontSize: 15 }} />}
                label="Top location"
                value={audience_short_desc?.location?.name || audience_short_desc?.location?.bucket || '–'}
                pct={audience_short_desc?.location?.percentage ?? ''}
              />
              <SnapshotRow
                icon={
                  (derivedGender?.code?.charAt(0) || derivedGender?.code?.[0]) === 'M' ? (
                    <ManIcon style={{ fontSize: 16 }} />
                  ) : (
                    <WomanIcon style={{ fontSize: 16 }} />
                  )
                }
                label="Top gender"
                value={genderLabel}
                pct={derivedGender?.percentage ?? ''}
              />
              <SnapshotRow
                icon={<CakeRoundedIcon style={{ fontSize: 14 }} />}
                label="Top age group"
                value={ageLabel}
                pct={audience_short_desc?.age?.percentage ?? ''}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BasicInfo;
